/**
 * Reading a response body while telling somebody how much of it has arrived.
 *
 * A progress bar is only honest about a download if it is fed the download
 * (`flight/boot-progress.ts`). `response.arrayBuffer()` gives no such signal —
 * it resolves once, at the end — so a caller that wants one reads the body in
 * chunks instead. Everyone else keeps the one-shot read, which is one copy where
 * this is three, and which is what every existing caller still gets.
 *
 * Its one user today is the enhanced airframe: 1.5 MB decoded, ~112 KB on the
 * wire since G48 published the fleet brotli-compressed, and the largest single
 * thing a cold `/fly` fetches (`docs/engineering/fly-boot-timing.md` §6). Its own
 * module rather than a private helper inside `enhanced-model.ts` because it is
 * about *transport*, not about models, and because nothing in it should have to
 * be re-derived by the next big fetch that wants a bar — including the
 * encoded-versus-decoded rule below, which is the trap this module walked into
 * once already.
 */

/** "This many of that many bytes have arrived." Called while the body is still streaming. */
export type BytesProgress = (loaded: number, total: number) => void;

/**
 * How much of the download must arrive before another report is made, as a
 * fraction of the whole.
 *
 * Chunk size is the network stack's business, not ours: measured through CDP
 * throttling, the same 1.46 MB file arrives in **23** chunks on a 1.6 Mbit link
 * and **311** on a 20 Mbit one. Each report costs a signal write and a repaint
 * decision on the thread the boot is trying to use, and under a heavy CPU
 * throttle — the case a progress bar exists for — that showed up in the boot's
 * own duration. A percent of a file this size is about a fifth of a drawn pixel
 * of the bar, so nothing visible is lost by coalescing to it.
 */
const REPORT_EVERY = 0.01;

/**
 * How many bytes the reader will hand over, or `0` when that cannot be known.
 *
 * **`Content-Length` is the length of the body on the wire, and the reader hands
 * over the body *decoded*.** For an identity response those are the same number
 * and the header is the answer. For a `Content-Encoding: br` response they are
 * not, and since G48 published the whole fleet brotli-compressed that is the
 * normal case for the one file this module exists to report on. Measured in a
 * real browser against the staged publish tree:
 *
 * | | |
 * |---|---|
 * | `Content-Length` | 111,897 |
 * | bytes out of the reader | 1,581,584 |
 * | where a bar driven by the header stops | **7.1 %** |
 *
 * and it stops there in silence for the other 93 %, which is exactly the
 * §6a percept — a bar that cannot move and a screen that says nothing — put back
 * by a change that never mentioned it.
 *
 * So the order is: the caller's own figure first, because only the producer of
 * the file knows its decoded length before the fetch starts
 * (`tools/enhanced` writes it into the manifest); then `Content-Length`, but
 * **only** when the response does not declare an encoding; then nothing.
 * Reporting nothing is a real answer here — `boot-progress.ts` falls back to its
 * easing and the caption keeps counting — and it is strictly better than
 * reporting a fraction of a denominator that is not the thing being counted.
 *
 * `Content-Encoding` is readable from JS for a same-origin response (measured:
 * it is in the enumerated header list for `/enhanced/*`), and a cross-origin
 * response that hides it hides `Content-Length` with it, so this cannot silently
 * fall through to the broken case.
 */
function reportableTotal(response: Response, decodedBytes: number | undefined): number {
  if (decodedBytes !== undefined && Number.isFinite(decodedBytes) && decodedBytes > 0) return decodedBytes;
  if (response.headers.get("Content-Encoding") !== null) return 0;
  const declared = Number(response.headers.get("Content-Length") ?? 0);
  return Number.isFinite(declared) && declared > 0 ? declared : 0;
}

/**
 * `response`'s body as one buffer, reporting progress along the way when
 * somebody is listening.
 *
 * Falls back to the one-shot read when there is no listener, no readable body,
 * or **no usable denominator** — there is nothing to be a fraction *of*, and
 * reporting against a denominator this function invented would be worse than
 * reporting nothing. `loaded` is still clamped to `total`, because a caller's
 * figure can be stale (a manifest written beside a `.glb` that was later
 * rebuilt); the final chunk always reports, so a bar driven by this lands
 * exactly where the download ends whichever way that clamp falls.
 *
 * @param decodedBytes what the caller knows the decoded body to be, if anything.
 */
export async function readStreamedBody(
  response: Response,
  onBytes: BytesProgress | undefined,
  decodedBytes?: number,
): Promise<ArrayBuffer> {
  const total = reportableTotal(response, decodedBytes);
  const body = response.body;
  if (!onBytes || !body || total <= 0) return await response.arrayBuffer();
  const reader = body.getReader();
  const step = Math.max(1, Math.floor(total * REPORT_EVERY));
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  let reported = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    if (loaded - reported < step && loaded < total) continue;
    reported = loaded;
    onBytes(Math.min(loaded, total), total);
  }
  const joined = new Uint8Array(loaded);
  let at = 0;
  for (const chunk of chunks) {
    joined.set(chunk, at);
    at += chunk.byteLength;
  }
  return joined.buffer;
}
